import React, { FC, useCallback, useState } from 'react';
import { Modal, DatePicker } from '@workboard/wobo-ui';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { TimeField } from '../datetime/TimeField';
import { useRemindMeForm } from './useRemindMeForm';
import {
  ItemContainer,
  InlineSelectField,
  InlineDateField,
} from '../form/InlineField';
import * as testIds from './testIds';

const Label = styled.label`
  display: block;
`;

const Wrapper = styled.div`
  width: 360px;
  height: 60px;
  box-sizing: border-box;

  & > div {
    position: absolute;
    width: calc(100% - 48px);
    min-width: initial;
  }
`;

interface IProps {
  aiId: string;
  initialValue?: Date;
  onClose?: () => void;
  onComplete?: () => void;
}

export const RemindMeModal: FC<IProps> = ({ aiId, onClose, onComplete }) => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(true);
  const { submitForm, getFieldProps, isProcessing, formErrors } =
    useRemindMeForm(aiId);

  const closeCallback = useCallback(() => {
    setOpen(false);
    onClose?.();
  }, [onClose]);

  const submitCallback = useCallback(async () => {
    try {
      await submitForm();
      setOpen(false);
      onComplete?.();
      setTimeout(() => onClose?.(), 5000);
    } catch (e) {
      console.error(e);
    }
  }, [onClose, submitForm, onComplete]);

  return (
    <Modal
      disableSubmit={Object.values(formErrors).length || isProcessing}
      removeSubmit={false}
      dismissLabel={t('Cancel')}
      isOpen={isOpen}
      onDismiss={closeCallback}
      onSubmit={submitCallback}
      required={false}
      submitLabel={t('Set reminder')}
      title={t('Remind me')}
    >
      <Wrapper>
        <ItemContainer items={2}>
          <InlineDateField data-testid={testIds.remindMeDateField}>
            <Label htmlFor="date">{t('Date')}</Label>
            <DatePicker
              {...getFieldProps('date', (val) =>
                val instanceof Date ? val.getTime() : new Date(val).getTime()
              )}
            />
          </InlineDateField>
          <InlineSelectField data-testid={testIds.remindMeTimeField}>
            <Label htmlFor="time">{t('Time')}</Label>
            <TimeField {...getFieldProps('time')} />
          </InlineSelectField>
        </ItemContainer>
      </Wrapper>
    </Modal>
  );
};
